import React, { useEffect, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'
import Modal from '../Modal'
import DropDown from '../DropDown'
import ColorItemPicker from '../Pickers/ColorPicker/components/ColorItemPicker'
import CloseIcon from '../../assets/icon/CloseIcon'
import DotsVerticalIcon from '../../assets/icon/DotsVerticalIcon'
import PaletteIcon from '../../assets/icon/PaletteIcon'
import ProjectService from '../../services/project.service'
import styles from './index.module.scss'
import {
    FIXED_PROJECT_STATUS_COLORS,
    FIXED_PROJECT_STATUS_KEYS,
    ProjectBoardStatus,
    ProjectTaskStatusItem,
    PROJECT_STATUS_COLORS,
    toProjectBoardStatuses,
    getProjectStatusColor,
    toProjectStatusPayload,
} from '../../utils/projectStatus'

const RESERVED_STATUS_COLORS = Object.values(FIXED_PROJECT_STATUS_COLORS)
const SELECTABLE_STATUS_COLORS = PROJECT_STATUS_COLORS.filter((color) => !RESERVED_STATUS_COLORS.includes(color))

interface ProjectStatusModalProps {
    isOpen: boolean
    projectId?: string | number | null
    statuses: ProjectTaskStatusItem[]
    onClose: () => void
    onSaved?: (statuses: ProjectBoardStatus[]) => void
}

interface StatusPreset {
    key: string
    title: string
    statuses: {
        key: string
        name: string
        color: string
        position: number
    }[]
}

let statusIdCounter = 0
const generateLocalId = () => `local_${Date.now()}_${++statusIdCounter}`

const cloneStatuses = (statuses: ProjectTaskStatusItem[]) => {
    return statuses.map((status, index) => ({
        id: status.id ?? generateLocalId(),
        key: status.key,
        name: status.name || status.title || '',
        color: getProjectStatusColor(status.key, status.color),
        position: status.position ?? index,
        is_mandatory: status.is_mandatory,
    }))
}

const getRandomItem = <T,>(items: T[]): T | undefined => {
    if (!items.length) return undefined
    return items[Math.floor(Math.random() * items.length)]
}

const getRandomAvailableColor = (
    statuses: ProjectTaskStatusItem[],
    excludedStatusIds: string[] = [],
    blockedColors: string[] = [],
) => {
    const excludedStatusIdSet = new Set(excludedStatusIds)
    const blockedColorSet = new Set(blockedColors)
    const usedColors = new Set(
        statuses
            .filter((status) => !excludedStatusIdSet.has(String(status.id)))
            .map((status) => status.color)
    )
    const availableColors = SELECTABLE_STATUS_COLORS.filter((color) => !usedColors.has(color) && !blockedColorSet.has(color))

    return getRandomItem(availableColors) || getRandomItem(SELECTABLE_STATUS_COLORS) || SELECTABLE_STATUS_COLORS[0]
}

const DragHandleIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="24" rx="12" fill="white"/>
        <path d="M10 6.00005C10 6.8285 9.32848 7.5001 8.50012 7.5001C7.67176 7.5001 7.00024 6.8285 7.00024 6.00005C7.00024 5.17159 7.67176 4.5 8.50012 4.5C9.32848 4.5 10 5.17159 10 6.00005Z" fill="currentColor"/>
        <path d="M9.99976 12.0012C9.99976 12.8297 9.32824 13.5013 8.49988 13.5013C7.67152 13.5013 7 12.8297 7 12.0012C7 11.1728 7.67152 10.5012 8.49988 10.5012C9.32824 10.5012 9.99976 11.1728 9.99976 12.0012Z" fill="currentColor"/>
        <path d="M9.99976 18C9.99976 18.8284 9.32824 19.5 8.49988 19.5C7.67152 19.5 7 18.8284 7 18C7 17.1715 7.67152 16.4999 8.49988 16.4999C9.32824 16.4999 9.99976 17.1715 9.99976 18Z" fill="currentColor"/>
        <path d="M17 6.00005C17 6.8285 16.3285 7.5001 15.5001 7.5001C14.6718 7.5001 14.0002 6.8285 14.0002 6.00005C14.0002 5.17159 14.6718 4.5 15.5001 4.5C16.3285 4.5 17 5.17159 17 6.00005Z" fill="currentColor"/>
        <path d="M16.9998 12.0012C16.9998 12.8297 16.3282 13.5013 15.4999 13.5013C14.6715 13.5013 14 12.8297 14 12.0012C14 11.1728 14.6715 10.5012 15.4999 10.5012C16.3282 10.5012 16.9998 11.1728 16.9998 12.0012Z" fill="currentColor"/>
        <path d="M16.9998 18C16.9998 18.8284 16.3282 19.5 15.4999 19.5C14.6715 19.5 14 18.8284 14 18C14 17.1715 14.6715 16.4999 15.4999 16.4999C16.3282 16.4999 16.9998 17.1715 16.9998 18Z" fill="currentColor"/>
    </svg>
)

const LockIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.9 4C12.9609 4 13.9783 4.42143 14.7284 5.17157C15.4786 5.92172 15.9 6.93913 15.9 8V10.4C16.5365 10.4 17.147 10.6529 17.5971 11.1029C18.0471 11.553 18.3 12.1635 18.3 12.8V17.6C18.3 18.2365 18.0471 18.847 17.5971 19.2971C17.147 19.7471 16.5365 20 15.9 20H7.9C7.26348 20 6.65303 19.7471 6.20294 19.2971C5.75286 18.847 5.5 18.2365 5.5 17.6V12.8C5.5 12.1635 5.75286 11.553 6.20294 11.1029C6.65303 10.6529 7.26348 10.4 7.9 10.4V8C7.9 6.93913 8.32143 5.92172 9.07157 5.17157C9.82172 4.42143 10.8391 4 11.9 4ZM11.9 13.6C11.4963 13.5999 11.1075 13.7523 10.8116 14.0268C10.5156 14.3013 10.3343 14.6775 10.304 15.08L10.3 15.2C10.3 15.5164 10.3938 15.8258 10.5696 16.0889C10.7455 16.352 10.9953 16.5571 11.2877 16.6782C11.5801 16.7993 11.9018 16.831 12.2121 16.7693C12.5225 16.7075 12.8076 16.5551 13.0314 16.3314C13.2551 16.1076 13.4075 15.8225 13.4693 15.5121C13.531 15.2018 13.4993 14.8801 13.3782 14.5877C13.2571 14.2953 13.052 14.0455 12.7889 13.8696C12.5258 13.6938 12.2165 13.6 11.9 13.6ZM11.9 5.6C11.2635 5.6 10.653 5.85286 10.2029 6.30294C9.75286 6.75303 9.5 7.36348 9.5 8V10.4H14.3V8C14.3 7.36348 14.0471 6.75303 13.5971 6.30294C13.147 5.85286 12.5365 5.6 11.9 5.6Z" fill="currentColor"/>
    </svg>
)

const RenameStatusIcon = () => (
    <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.3772 0.729439L15.2713 1.62358C15.7495 2.10175 15.7495 2.93527 15.2713 3.41272L13.9693 4.71479L11.3008 2.04631L12.6028 0.744237C12.8419 0.505174 13.158 0.373047 13.497 0.373047C13.8345 0.373047 14.1386 0.505174 14.3772 0.729439Z" fill="currentColor"/>
        <path d="M10.4023 2.93018L3.92464 9.40781C3.88318 9.44927 3.85397 9.50106 3.8408 9.55755L3.15599 12.4607C3.13151 12.5662 3.16256 12.6772 3.23975 12.7544C3.29908 12.8137 3.37911 12.8466 3.46196 12.8466C3.48542 12.8466 3.5098 12.8438 3.53334 12.8381L6.43653 12.1533C6.49299 12.1401 6.54569 12.1109 6.58617 12.0694L13.0638 5.5918L10.4023 2.93018Z" fill="currentColor"/>
        <path d="M15.8402 15.4007H7.67056C7.32367 15.4007 7.04199 15.119 7.04199 14.7721C7.04199 14.4252 7.32367 14.1436 7.67056 14.1436H15.8402C16.1871 14.1436 16.4688 14.4252 16.4688 14.7721C16.4688 15.1201 16.1871 15.4007 15.8402 15.4007Z" fill="currentColor"/>
    </svg>
)

const TrashStatusIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.2 8.8C6.2 8.68954 6.28954 8.6 6.4 8.6H17.7111C17.8216 8.6 17.9111 8.68954 17.9111 8.8V19.65C17.9111 19.8754 17.8216 20.0916 17.6622 20.251C17.5027 20.4104 17.2865 20.5 17.0611 20.5H7.05C6.82457 20.5 6.60837 20.4104 6.44896 20.251C6.28955 20.0916 6.2 19.8754 6.2 19.65V8.8ZM8.75 6.05V4.35C8.75 4.12457 8.83955 3.90837 8.99896 3.74896C9.15837 3.58955 9.37457 3.5 9.6 3.5H14.5111C14.7365 3.5 14.9527 3.58955 15.1122 3.74896C15.2716 3.90837 15.3611 4.12457 15.3611 4.35V6.05H18.7611C19.2306 6.05 19.6111 6.43056 19.6111 6.9C19.6111 7.36944 19.2306 7.75 18.7611 7.75H5.35C4.88056 7.75 4.5 7.36944 4.5 6.9C4.5 6.43056 4.88056 6.05 5.35 6.05H8.75ZM10.65 5.2C10.5395 5.2 10.45 5.28954 10.45 5.4V5.85C10.45 5.96046 10.5395 6.05 10.65 6.05H13.4611C13.5716 6.05 13.6611 5.96046 13.6611 5.85V5.4C13.6611 5.28954 13.5716 5.2 13.4611 5.2H10.65ZM10.3556 12C9.88611 12 9.50556 12.3806 9.50556 12.85V16.25C9.50556 16.7194 9.88611 17.1 10.3556 17.1C10.825 17.1 11.2056 16.7194 11.2056 16.25V12.85C11.2056 12.3806 10.825 12 10.3556 12ZM13.7556 12C13.2861 12 12.9056 12.3806 12.9056 12.85V16.25C12.9056 16.7194 13.2861 17.1 13.7556 17.1C14.225 17.1 14.6056 16.7194 14.6056 16.25V12.85C14.6056 12.3806 14.225 12 13.7556 12Z" fill="#F04438"/>
    </svg>
)

export default function ProjectStatusModal({
    isOpen,
    projectId,
    statuses,
    onClose,
    onSaved,
}: ProjectStatusModalProps) {
    const [localStatuses, setLocalStatuses] = useState<ProjectTaskStatusItem[]>([])
    const [presets, setPresets] = useState<StatusPreset[]>([])
    const [presetKey, setPresetKey] = useState<string>('')
    const [presetsOpen, setPresetsOpen] = useState(false)
    const presetsRef = useRef<HTMLDivElement>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [openMenuId, setOpenMenuId] = useState<string | null>(null)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [editingName, setEditingName] = useState('')
    const [colorPickerId, setColorPickerId] = useState<string | null>(null)
    const [dragItemId, setDragItemId] = useState<string | null>(null)
    const [dragOverId, setDragOverId] = useState<string | null>(null)
    const editInputRef = useRef<HTMLInputElement>(null)
    const statusRowRefs = useRef<Map<string, HTMLDivElement>>(new Map())
    const colorPickerPortalRef = useRef<HTMLDivElement>(null)
    const [colorPickerPortalPos, setColorPickerPortalPos] = useState<{ top: number; left: number } | null>(null)

    useEffect(() => {
        if (isOpen) {
            setLocalStatuses(cloneStatuses(statuses))
            setPresetKey('')
            setPresetsOpen(false)
            setOpenMenuId(null)
            setEditingId(null)
            setEditingName('')
            setColorPickerId(null)
            setDragItemId(null)
            setDragOverId(null)
        }
    }, [isOpen, statuses])

    useEffect(() => {
        if (!isOpen) return

        const fetchPresets = async () => {
            try {
                const data = await ProjectService.getStatusPresets()
                setPresets(data.presets || [])
            } catch (error) {
                console.error('Failed to fetch status presets:', error)
            }
        }

        fetchPresets()
    }, [isOpen])

    useEffect(() => {
        if (!presetsOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            if (presetsRef.current && !presetsRef.current.contains(event.target as Node)) {
                setPresetsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [presetsOpen])

    useEffect(() => {
        if (!editingId) return

        const frameId = window.requestAnimationFrame(() => {
            const input = editInputRef.current
            if (!input) return

            input.focus()
            const cursorPosition = input.value.length
            input.setSelectionRange(cursorPosition, cursorPosition)
        })

        return () => window.cancelAnimationFrame(frameId)
    }, [editingId])

    useEffect(() => {
        if (!colorPickerId) {
            setColorPickerPortalPos(null)
            return
        }

        const statusRow = statusRowRefs.current.get(colorPickerId)
        if (!statusRow) {
            setColorPickerPortalPos(null)
            return
        }

        const rect = statusRow.getBoundingClientRect()
        setColorPickerPortalPos({
            top: rect.bottom + 8,
            left: rect.left,
        })
    }, [colorPickerId])

    useEffect(() => {
        if (!colorPickerId) return

        const handleClickOutside = (event: MouseEvent) => {
            if (colorPickerPortalRef.current && !colorPickerPortalRef.current.contains(event.target as Node)) {
                setColorPickerId(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [colorPickerId])

    useEffect(() => {
        if (!colorPickerId) return

        const handleScroll = () => setColorPickerId(null)

        window.addEventListener('scroll', handleScroll, true)
        return () => window.removeEventListener('scroll', handleScroll, true)
    }, [colorPickerId])

    const isFixed = (status: ProjectTaskStatusItem) => {
        return Boolean(status.is_mandatory) || FIXED_PROJECT_STATUS_KEYS.includes(status.key)
    }

    const isPositionLocked = (status: ProjectTaskStatusItem) => {
        return status.key === 'backlog' || status.key === 'complete'
    }

    const canDrag = (status: ProjectTaskStatusItem, index: number) => {
        return index !== 0 && !isPositionLocked(status)
    }

    const handleClose = () => {
        if (!isSaving) {
            onClose()
        }
    }

    const selectedPreset = presets.find((preset) => preset.key === presetKey)

    const selectPreset = (key: string) => {
        setPresetKey(key)
        setPresetsOpen(false)

        const preset = presets.find((item) => item.key === key)
        if (!preset || !preset.statuses.length) return

        const sorted = [...preset.statuses].sort((a, b) => a.position - b.position)

        setLocalStatuses(sorted.map((status, index) => ({
            id: generateLocalId(),
            key: status.key,
            name: status.name,
            color: getProjectStatusColor(status.key, status.color),
            position: index,
            is_mandatory: FIXED_PROJECT_STATUS_KEYS.includes(status.key),
        })))

        setOpenMenuId(null)
        setEditingId(null)
        setEditingName('')
        setColorPickerId(null)
    }

    const handleAddStatus = () => {
        if (localStatuses.length >= FIXED_PROJECT_STATUS_KEYS.length + SELECTABLE_STATUS_COLORS.length) return

        const newStatus = {
            id: generateLocalId(),
            key: `custom_status_${Date.now()}`,
            name: '',
            color: getRandomAvailableColor(localStatuses),
            position: localStatuses.length,
        }

        setLocalStatuses((prev) => {
            const completeIndex = prev.findIndex((status) => status.key === 'complete')
            if (completeIndex === -1) {
                return [...prev, newStatus]
            }

            const next = [...prev]
            next.splice(completeIndex, 0, newStatus)
            return next
        })
        setEditingId(String(newStatus.id))
        setEditingName('')
        setOpenMenuId(null)
    }

    const closeStatusActionMenu = () => {
        flushSync(() => {
            setOpenMenuId(null)
        })
    }

    const startStatusRename = (status: ProjectTaskStatusItem) => {
        closeStatusActionMenu()
        setEditingId(String(status.id))
        setEditingName(status.name || status.title || '')
        setColorPickerId(null)
    }

    const finishStatusRename = () => {
        if (!editingId || !editingName.trim()) {
            setEditingId(null)
            setEditingName('')
            return
        }

        setLocalStatuses((prev) => prev.map((status) => (
            String(status.id) === editingId
                ? { ...status, name: editingName.trim() }
                : status
        )))

        setEditingId(null)
        setEditingName('')
    }

    const changeStatusColor = (statusId: string, color: string) => {
        setLocalStatuses((prev) => {
            const target = prev.find((status) => String(status.id) === statusId)
            if (!target || isFixed(target)) return prev

            const conflict = prev.find((status) => String(status.id) !== statusId && status.color === color)
            if (conflict && isFixed(conflict)) return prev

            return prev.map((status) => {
                if (String(status.id) === statusId) {
                    return { ...status, color }
                }

                if (conflict && String(status.id) === String(conflict.id) && !isFixed(status)) {
                    return {
                        ...status,
                        color: getRandomAvailableColor(prev, [statusId, String(status.id)], [color]),
                    }
                }

                return status
            })
        })
        setColorPickerId(null)
        closeStatusActionMenu()
    }

    const openStatusColorPicker = (statusId: string) => {
        closeStatusActionMenu()
        setColorPickerId(statusId)
    }

    const deleteStatus = (statusId: string) => {
        setLocalStatuses((prev) => prev.filter((status) => String(status.id) !== statusId))
        closeStatusActionMenu()
        setColorPickerId(null)
    }

    const handleDragStart = (event: React.DragEvent, id: string) => {
        setDragItemId(id)
        event.dataTransfer.effectAllowed = 'move'
    }

    const handleDragOver = (event: React.DragEvent, id: string, index: number) => {
        event.preventDefault()
        if (!canDrag(localStatuses[index], index) || id === dragItemId) return
        setDragOverId(id)
    }

    const handleDrop = (event: React.DragEvent, targetId: string, targetIndex: number) => {
        event.preventDefault()
        setDragOverId(null)

        if (!dragItemId || dragItemId === targetId || !canDrag(localStatuses[targetIndex], targetIndex)) {
            setDragItemId(null)
            return
        }

        const dragIndex = localStatuses.findIndex((status) => String(status.id) === dragItemId)
        const dropIndex = localStatuses.findIndex((status) => String(status.id) === targetId)

        if (dragIndex === -1 || dropIndex === -1) {
            setDragItemId(null)
            return
        }

        const nextStatuses = [...localStatuses]
        const [draggedStatus] = nextStatuses.splice(dragIndex, 1)
        nextStatuses.splice(dropIndex, 0, draggedStatus)

        setLocalStatuses(nextStatuses)
        setDragItemId(null)
    }

    const saveStatuses = async () => {
        if (!projectId) return

        setIsSaving(true)
        try {
            await ProjectService.updateTaskStatuses(String(projectId), toProjectStatusPayload(localStatuses))
            onSaved?.(toProjectBoardStatuses(localStatuses))
            onClose()
        } catch (error) {
            console.error('Failed to save statuses:', error)
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <>
        <Modal isOpen={isOpen} onRequestClose={handleClose} width={446}>
            <div className={styles.statusModal}>
                <div className={styles.statusModalClose} onClick={handleClose}>
                    <CloseIcon />
                </div>
                <div className={styles.statusModalHeader}>
                    <h3 className={styles.statusModalTitle}>Настройка статусов внутри проекта</h3>
                </div>
                <div className={styles.statusPreset}>
                    <div className={styles.statusPresetLabel}>Статусы задач</div>
                    <div className={styles.statusPresetSelect} ref={presetsRef}>
                        <div
                            className={[
                                styles.statusPresetTrigger,
                                presetsOpen ? styles.statusPresetTriggerOpen : '',
                            ].join(' ').trim()}
                            onClick={() => setPresetsOpen((prev) => !prev)}
                        >
                            <span className={selectedPreset ? styles.statusPresetTriggerValue : styles.statusPresetTriggerPlaceholder}>
                                {selectedPreset ? selectedPreset.title : 'Настраиваемые статусы'}
                            </span>
                            <svg
                                width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                                style={{
                                    transform: presetsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                }}
                            >
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        {presetsOpen && (
                            <div className={styles.statusPresetDropdown}>
                                {presets.map((preset) => (
                                    <div
                                        key={preset.key}
                                        className={[
                                            styles.statusPresetItem,
                                            presetKey === preset.key ? styles.statusPresetItemActive : '',
                                        ].join(' ').trim()}
                                        onClick={() => selectPreset(preset.key)}
                                    >
                                        <span>{preset.title}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.statusList}>
                    {localStatuses.map((status, index) => {
                        const statusId = String(status.id)
                        const isDraggable = canDrag(status, index)
                        const fixedStatus = isFixed(status)

                        return (
                            <div
                                key={statusId}
                                ref={(element) => {
                                    if (element) {
                                        statusRowRefs.current.set(statusId, element)
                                    } else {
                                        statusRowRefs.current.delete(statusId)
                                    }
                                }}
                                className={[
                                    styles.statusItem,
                                    dragItemId === statusId ? styles.statusItemDragging : '',
                                    dragOverId === statusId ? styles.statusItemDragOver : '',
                                ].join(' ').trim()}
                                draggable={isDraggable}
                                onDragStart={isDraggable ? (event) => handleDragStart(event, statusId) : undefined}
                                onDragOver={isDraggable ? (event) => handleDragOver(event, statusId, index) : undefined}
                                onDragLeave={() => setDragOverId(null)}
                                onDrop={isDraggable ? (event) => handleDrop(event, statusId, index) : undefined}
                                onDragEnd={() => {
                                    setDragItemId(null)
                                    setDragOverId(null)
                                }}
                            >
                                {isPositionLocked(status) ? (
                                    <div className={styles.statusLock}>
                                        <LockIcon />
                                    </div>
                                ) : (
                                    <div className={[
                                        styles.statusDragHandle,
                                        !isDraggable ? styles.statusDragHandleDisabled : '',
                                    ].join(' ').trim()}>
                                        <DragHandleIcon />
                                    </div>
                                )}
                                <div className={styles.statusDot} style={{ background: status.color }} />
                                {editingId === statusId ? (
                                    <input
                                        ref={editInputRef}
                                        className={styles.statusInput}
                                        value={editingName}
                                        onChange={(event) => setEditingName(event.target.value)}
                                        onBlur={finishStatusRename}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') finishStatusRename()
                                            if (event.key === 'Escape') {
                                                setEditingId(null)
                                                setEditingName('')
                                            }
                                        }}
                                    />
                                ) : (
                                    <span className={styles.statusName}>{status.name || status.title}</span>
                                )}
                                {!fixedStatus && (
                                    <DropDown
                                        isOpen={openMenuId === statusId}
                                        setIsOpen={(nextIsOpen) => {
                                            setOpenMenuId(nextIsOpen ? statusId : null)
                                            setColorPickerId(null)
                                        }}
                                        position="right"
                                        dropdownClassName={styles.statusPortalDropdown}
                                        contentClassName={styles.statusDropdownContent}
                                        btn={
                                            <div className={styles.statusDots}>
                                                <DotsVerticalIcon />
                                            </div>
                                        }
                                    >
                                        <div className={styles.statusDropdown}>
                                            <div
                                                className={styles.statusDropdownItem}
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    startStatusRename(status)
                                                }}
                                            >
                                                <span className={styles.statusDropdownIconBox}>
                                                    <RenameStatusIcon />
                                                </span>
                                                <span>Переименовать</span>
                                            </div>
                                            <div
                                                className={styles.statusDropdownItem}
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    openStatusColorPicker(statusId)
                                                }}
                                            >
                                                <span className={styles.statusDropdownIconBox}>
                                                    <PaletteIcon />
                                                </span>
                                                <span>Изменить цвет</span>
                                            </div>
                                            <div
                                                className={[styles.statusDropdownItem, styles.statusDropdownItemDanger].join(' ')}
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    deleteStatus(statusId)
                                                }}
                                            >
                                                <span className={styles.statusDropdownIconBox}>
                                                    <TrashStatusIcon />
                                                </span>
                                                <span>Удалить</span>
                                            </div>
                                        </div>
                                    </DropDown>
                                )}
                            </div>
                        )
                    })}
                </div>
                <div
                    className={[
                        styles.addStatusButton,
                        localStatuses.length >= FIXED_PROJECT_STATUS_KEYS.length + SELECTABLE_STATUS_COLORS.length ? styles.addStatusButtonDisabled : '',
                    ].join(' ').trim()}
                    onClick={handleAddStatus}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M8 1C8.36819 1 8.66667 1.29848 8.66667 1.66667V14.3333C8.66667 14.7015 8.36819 15 8 15C7.63181 15 7.33333 14.7015 7.33333 14.3333V1.66667C7.33333 1.29848 7.63181 1 8 1Z" fill="currentColor" />
                        <path fillRule="evenodd" clipRule="evenodd" d="M1 8C1 7.63181 1.29848 7.33333 1.66667 7.33333H14.3333C14.7015 7.33333 15 7.63181 15 8C15 8.36819 14.7015 8.66667 14.3333 8.66667H1.66667C1.29848 8.66667 1 8.36819 1 8Z" fill="currentColor" />
                    </svg>
                    Добавить статус
                </div>
                <div className={styles.statusModalActions}>
                    <button className={styles.statusSecondaryButton} onClick={handleClose} disabled={isSaving}>
                        Отменить
                    </button>
                    <button className={styles.statusPrimaryButton} onClick={saveStatuses} disabled={isSaving}>
                        {isSaving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            </div>
        </Modal>
        {colorPickerId && colorPickerPortalPos && typeof document !== 'undefined' && createPortal(
            <div
                ref={colorPickerPortalRef}
                className={styles.statusColorPickerPortal}
                style={{ top: colorPickerPortalPos.top, left: colorPickerPortalPos.left }}
            >
                <div className={styles.statusColorPickerTitle}>Выбор цвета</div>
                <div className={styles.statusColorPickerGrid}>
                    {SELECTABLE_STATUS_COLORS.map((color) => (
                        <ColorItemPicker
                            key={color}
                            currentColor={color}
                            isActive={localStatuses.find((status) => String(status.id) === colorPickerId)?.color === color}
                            getColor={() => changeStatusColor(colorPickerId!, color)}
                        />
                    ))}
                </div>
            </div>,
            document.body
        )}
        </>
    )
}
